/**
 * Maps canonical DB values (English keys stored in retail_target_skus) to
 * i18n-aware display labels. The DB always stores the English canonical
 * (`generic`, `premium`, `organic`) — translation happens at the view layer.
 */

type T = (key: string) => string;

export function getTierLabel(t: T, tier: string | null | undefined): string {
  if (!tier) return t('Sin tier');
  switch (tier) {
    case 'generic':
      return t('Genérico');
    case 'premium':
      return t('Premium');
    case 'organic':
      return t('Orgánico');
    default:
      return tier;
  }
}

// Cache one Intl.DisplayNames per locale — building it is relatively heavy.
const countryDisplayCache = new Map<string, Intl.DisplayNames>();

/**
 * Full country name from an ISO 3166-1 alpha-2 code (e.g. "MX" → "México").
 * Used in the retail chart legend instead of the bare code. Falls back to
 * the original value if it can't be resolved.
 */
export function getCountryLabel(
  code: string | null | undefined,
  locale = 'es'
): string {
  if (!code) return '—';
  const trimmed = code.trim();
  if (trimmed.length !== 2) return trimmed;
  try {
    let dn = countryDisplayCache.get(locale);
    if (!dn) {
      dn = new Intl.DisplayNames([locale], { type: 'region' });
      countryDisplayCache.set(locale, dn);
    }
    return dn.of(trimmed.toUpperCase()) ?? trimmed;
  } catch {
    return trimmed;
  }
}

// ---- Regiones ----
// ÚNICA fuente de verdad país → región para retail. El gráfico, las tabs y
// cualquier consumidor futuro (exportes, agentes) importan todo de acá:
// mapping, orden, colores y labels.

export type RetailRegion =
  | 'north_america'
  | 'latam'
  | 'europe'
  | 'middle_east'
  | 'asia'
  | 'oceania'
  | 'other';

export const COUNTRY_REGIONS: Record<string, RetailRegion> = {
  US: 'north_america',
  CA: 'north_america',
  // México se lee como mercado latinoamericano para la fruta chilena;
  // moverlo a north_america es cambiar solo esta línea.
  MX: 'latam',
  BR: 'latam',
  CL: 'latam',
  CO: 'latam',
  EC: 'latam',
  PE: 'latam',
  AT: 'europe',
  BE: 'europe',
  BG: 'europe',
  CH: 'europe',
  DE: 'europe',
  ES: 'europe',
  FI: 'europe',
  FR: 'europe',
  GB: 'europe',
  GR: 'europe',
  IE: 'europe',
  IT: 'europe',
  NL: 'europe',
  PT: 'europe',
  SE: 'europe',
  SK: 'europe',
  AE: 'middle_east',
  SA: 'middle_east',
  CN: 'asia',
  HK: 'asia',
  IN: 'asia',
  JP: 'asia',
  KR: 'asia',
  SG: 'asia',
  TW: 'asia',
  AU: 'oceania',
  NZ: 'oceania',
};

/** Países no mapeados caen en 'other' — visible en el gráfico, nunca rompe. */
export function getRegionForCountry(
  country: string | null | undefined
): RetailRegion {
  return (country && COUNTRY_REGIONS[country.trim().toUpperCase()]) || 'other';
}

/** Orden de despliegue (mercados grandes primero) para leyendas y series. */
export const REGION_ORDER: RetailRegion[] = [
  'north_america',
  'latam',
  'europe',
  'middle_east',
  'asia',
  'oceania',
  'other',
];

/** Color estable por región (no depende del orden de llegada de la data). */
export const REGION_CHART_COLORS: Record<RetailRegion, string> = {
  north_america: '#2563eb',
  latam: '#e11d48',
  europe: '#16a34a',
  middle_east: '#ca8a04',
  asia: '#9333ea',
  oceania: '#0891b2',
  other: '#6b7280',
};

export function getRegionLabel(t: T, region: RetailRegion): string {
  switch (region) {
    case 'north_america':
      return t('Norteamérica');
    case 'latam':
      return t('Latinoamérica');
    case 'europe':
      return t('Europa');
    case 'middle_east':
      return t('Medio Oriente');
    case 'asia':
      return t('Asia');
    case 'oceania':
      return t('Oceanía');
    default:
      return t('Otros');
  }
}
